import React, { useEffect, useRef, useState } from "react";
import styles from "./styles/AssetManagement.module.css";

/*
  A toolbar filter that accepts several values at once.

  It replaces a native <select> per filter, which could only ever hold one
  choice: picking Bank Account meant giving up Real Estate, so answering "what
  do I hold in these two categories" took two passes and a mental total. A
  checkbox list answers it in one.

  The button has to stay the width of a toolbar control no matter how many
  values are ticked, so it shows the first choice and a count of the rest --
  "Bank Account +2". The full list is in the title attribute for anyone who
  wants to read it without opening the menu.
*/

/*
  Does this value survive the filter?

  An EMPTY selection means "no filter", not "match nothing" -- the distinction
  is the whole contract, and getting it backwards empties the table instead of
  filling it. Written once and shared by both filters rather than inlined twice.
*/
export const matchesSelection = (value, selected) => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.includes(value);
};

/*
  What the closed button reads: the first choice, plus how many more.

  A value with no matching option still counts and still shows -- a custom asset
  type the user renamed is stale, not absent, and silently dropping it from the
  summary would leave the table filtered by something the button denies.
*/
export const selectionSummary = (selected, options) => {
  const list = Array.isArray(selected) ? selected : [];
  const labelFor = (value) => {
    const option = (options || []).find((o) => o && o.value === value);
    return (option && option.label) || value;
  };
  return {
    text: list.length ? labelFor(list[0]) : "",
    extra: Math.max(list.length - 1, 0),
    all: list.map(labelFor),
  };
};

export default function MultiSelectFilter({
  label,
  options = [],
  selected = [],
  onChange,
  clearLabel,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  /*
    Bound only while the menu is open, so a page with several of these is not
    running a document listener each for a menu nobody opened.
  */
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const list = Array.isArray(selected) ? selected : [];
  const active = list.length > 0;
  const { text, extra, all } = selectionSummary(list, options);

  const toggle = (value) =>
    onChange(
      list.includes(value)
        ? list.filter((current) => current !== value)
        : [...list, value]
    );

  return (
    <div className={styles.filter_wrap} ref={ref}>
      <button
        type="button"
        className={`${styles.filter_button} ${
          active ? styles.filter_active : ""
        }`}
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-haspopup="true"
        title={active ? all.join(", ") : label}
      >
        <span className={styles.filter_button_label}>
          {active ? text : label}
        </span>
        {extra > 0 && <span className={styles.filter_count}>+{extra}</span>}
      </button>

      {/*
        The caret and the ✕ share one slot: the caret says "there is a list
        here", the ✕ says "and you can get out of it". Showing both would
        crowd, and the ✕ is the one that matters once a filter is on.
      */}
      {active ? (
        <button
          type="button"
          className={styles.filter_clear}
          onClick={() => onChange([])}
          aria-label={clearLabel}
          title={clearLabel}
        >
          ✕
        </button>
      ) : (
        <span className={styles.filter_caret} aria-hidden="true">
          ▾
        </span>
      )}

      {open && (
        <div className={styles.fields_menu} role="group" aria-label={label}>
          {/*
            The way out from inside the menu, kept from the native select this
            replaced -- its blank option doubled as "Clear filter", so opening
            the list always offered a way back to unfiltered.

            Always rendered, dimmed and disabled while nothing is chosen, rather
            than appearing on the first tick: mounting it then pushes every
            option down a row, so the box you are about to tick moves out from
            under the pointer.
          */}
          <button
            type="button"
            className={`${styles.fields_option} ${styles.filter_menu_clear}`}
            onClick={() => onChange([])}
            disabled={!active}
          >
            {clearLabel}
          </button>
          {options.map((option) => (
            <label key={option.value} className={styles.fields_option}>
              <input
                type="checkbox"
                checked={list.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
